import api from "@/lib/api";

// This is an operation identity, never an auth token. Persist BEFORE POST so a
// lost response/reload cannot silently create another paid collection.
const key = (user, action) => `op_collection_v1:${user?.id}:${user?.workspace_id || "personal"}:${action}`;
const terminal = (record) => ["completed", "failed_pre_send", "abandoned"].includes(record.state) || (record.state === "not_admitted" && record.admission === "not_admitted");
export function storedCollection(user, action) {
  const text = localStorage.getItem(key(user, action));
  if (!text) return null;
  const value = JSON.parse(text);
  if (!value || typeof value.id !== "string" || !value.parameters || typeof value.state !== "string") throw new Error("Identité de collecte locale illisible : aucune nouvelle soumission autorisée.");
  return value;
}
export function collectionScope(user) { return `${user?.id}:${user?.workspace_id || "personal"}`; }
function store(user, action, value) { localStorage.setItem(key(user, action), JSON.stringify(value)); }
function remember(user, action, record, operation) {
  if (operation?.id !== record.id || typeof operation.state !== "string"
      || (operation.state === "not_admitted" && operation.admission !== "not_admitted")) {
    throw new Error("Réponse d’opération invalide ; conservez cette identité et consultez son état.");
  }
  const latest = storedCollection(user, action);
  if (latest?.id !== record.id || (Number.isInteger(latest.version) && latest.version > operation.version)) return;
  const { state, version, admission, can_continue, requires_review, message } = operation;
  store(user, action, { ...record, state, version, admission, can_continue, requires_review, message });
}
export async function submitCollection(user, action, path, parameters = {}, { newBatch = false } = {}) {
  const previous = storedCollection(user, action);
  if (newBatch && (previous?.state !== "stopped" || previous.requires_review)) throw new Error("Un nouveau lot exige un arrêt connu et sans issue incertaine.");
  if (!newBatch && previous && !terminal(previous) && JSON.stringify(previous.parameters) !== JSON.stringify(parameters)) {
    throw new Error("Une collecte non résolue possède d’autres paramètres. Consultez son état avant une nouvelle opération.");
  }
  const record = !newBatch && previous && !terminal(previous) ? previous : { id: crypto.randomUUID(), parameters, state: "pending" };
  store(user, action, record); // Storage failure refuses BEFORE transmission.
  try {
    const { data } = await api.post(path, parameters, { headers: { "Idempotency-Key": record.id } });
    if (!data?.operation || data.operation.id !== record.id) throw new Error("Réponse d’opération invalide ; consultez son état sans relancer.");
    remember(user, action, record, data.operation);
    return data;
  } catch (error) {
    const operation = error?.response?.data?.operation;
    if (operation?.id === record.id) remember(user, action, record, operation);
    throw error;
  }
}
export async function refreshCollection(user, action) {
  const record = storedCollection(user, action);
  if (!record) throw new Error("Aucune identité de collecte enregistrée.");
  try {
    const { data } = await api.get(`/ingest/operations/${encodeURIComponent(record.id)}`);
    if (data?.operation?.id !== record.id) throw new Error("État d’opération invalide.");
    remember(user, action, record, data.operation);
    return data;
  } catch (error) {
    const operation = error?.response?.data?.operation;
    if (operation?.id === record.id) remember(user, action, record, operation);
    throw error;
  }
}

// This is an explicit mutation, NEVER called by a GET/status refresh. Retain the
// batch identity and expected version across a lost continuation response.
export async function continueCollection(user, action) {
  const record = storedCollection(user, action);
  if (record?.state !== "stopped" || !record.can_continue || record.requires_review || !Number.isInteger(record.version)) {
    throw new Error("Consultez un lot arrêté sans issue incertaine avant de continuer.");
  }
  const { data } = await api.post(`/ingest/operations/${encodeURIComponent(record.id)}/continue`, { expected_version: record.version });
  remember(user, action, record, data.operation);
  return data;
}
